import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  FileText,
  Folder,
  Mail,
  MessageSquareText,
  Pencil,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { api, ApiError } from "../../api";
import { useAuth, hasAnyRole } from "../../auth";
import { Button, Card, cx } from "../../ui";

/**
 * Per-user detail page reachable by clicking a row from
 * /admin/users. Surfaces every artefact the user touches across the
 * buildings the principal manages: profile + ownerships + projects
 * + insurance policies + invites (issued and received) +
 * correspondence (notifications and comments).
 *
 * Platform admins see the unrestricted /api/admin/users/:userId
 * profile; PMs see /api/management/users/:userId, which filters
 * grants and ownerships down to the buildings they actually
 * manage. Linked-item endpoints under /api/management/* return
 * the same building-scoped data to both.
 */

type Tab =
  | "profile"
  | "projects"
  | "insurance"
  | "documents"
  | "invites"
  | "correspondence";

const TAB_ORDER: ReadonlyArray<{ id: Tab; label: string; icon: JSX.Element }> = [
  { id: "profile", label: "Profile", icon: <UserRound size={14} /> },
  { id: "projects", label: "Projects", icon: <Folder size={14} /> },
  { id: "insurance", label: "Insurance", icon: <ShieldCheck size={14} /> },
  { id: "documents", label: "Documents", icon: <FileText size={14} /> },
  { id: "invites", label: "Invites", icon: <Mail size={14} /> },
  {
    id: "correspondence",
    label: "Correspondence",
    icon: <MessageSquareText size={14} />,
  },
];

type RoleGrant = {
  id: string;
  roleCode: string;
  roleName: string;
  buildingId: string;
  buildingName: string;
  grantedAt: string;
  /** True for synthesized unit_owner grants derived from an
   *  active ownership row. The user is effectively a unit owner
   *  even though no explicit user_roles record exists. */
  implicit?: boolean;
};
type Ownership = {
  id: string;
  unitId: string;
  unitNumber: string;
  buildingId: string;
  buildingName: string;
  ownershipType: string;
  startedAt: string;
};
type UserDetail = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  isActive: boolean;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  roles: RoleGrant[];
  ownerships: Ownership[];
};

export function AdminUserDetail() {
  const { userId = "" } = useParams<{ userId: string }>();
  const [params, setParams] = useSearchParams();
  const { state } = useAuth();
  const isAdmin = state.status === "authenticated" && hasAnyRole(state.me, ["admin"]);

  const tab: Tab = (TAB_ORDER.some((t) => t.id === params.get("tab"))
    ? (params.get("tab") as Tab)
    : "profile") as Tab;

  const setTab = (next: Tab) => {
    const updated = new URLSearchParams(params);
    if (next === "profile") updated.delete("tab");
    else updated.set("tab", next);
    setParams(updated, { replace: true });
  };

  const [user, setUser] = useState<UserDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setUser(null);
    setLoadError(null);
    // Admins see the cross-building view; PMs go through the
    // building-scoped management endpoint.
    const url = isAdmin
      ? `/api/admin/users/${userId}`
      : `/api/management/users/${userId}`;
    api
      .get<{ data: UserDetail }>(url)
      .then((r) => setUser(r.data))
      .catch((err) => {
        if (err instanceof ApiError) {
          setLoadError(
            err.status === 403
              ? "You don't have access to this user."
              : err.status === 404
                ? "User not found."
                : `Load failed (${err.status})`,
          );
        } else setLoadError(String(err));
      });
  }, [userId, isAdmin]);

  return (
    <div>
      {/*
        Secondary header for the user-detail surface. Uses a lighter
        brand green (brand-600) than the parent AdminLayout's dark
        brand-800 strip so the two headers read as a hierarchy
        rather than one continuous slab. Renders the "All users"
        back link inline at the top — the previous layout placed
        the link in normal flow above the header, but PageHeader's
        -mt-6 bleed yanked the strip up over the link and clipped
        it; pulling the link inside fixes the alignment.
      */}
      <div className="-mt-6 -mx-4 sm:-mx-6 lg:-mx-8 mb-6 bg-brand-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-5 pb-7">
          <Link
            to="/admin/users"
            className="inline-flex items-center gap-1 text-[12px] text-white/70 hover:text-white no-underline hover:no-underline mb-3"
          >
            <ArrowLeft size={13} /> All users
          </Link>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-white m-0">
                {user ? `${user.firstName} ${user.lastName}` : "User"}
              </h1>
              {user && (
                <p className="mt-1 text-[12.5px] text-white/70 m-0">
                  {user.email}
                </p>
              )}
            </div>
            {user && (
              <Link
                to={`/admin/users/${user.id}/edit`}
                className="btn-on-dark btn-sm no-underline hover:no-underline"
              >
                <Pencil size={13} /> Edit
              </Link>
            )}
          </div>
        </div>
      </div>

      <nav className="flex gap-1 mb-4 overflow-x-auto -mx-1 px-1">
        {TAB_ORDER.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cx(
              "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors",
              tab === t.id
                ? "bg-white text-brand-700 border border-brand-200 shadow-soft"
                : "bg-transparent text-ink-600 hover:text-ink-800 hover:bg-white border border-transparent",
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </nav>

      {loadError && (
        <Card>
          <p className="text-status-rejected-fg text-sm">{loadError}</p>
        </Card>
      )}

      {!loadError && !user && (
        <Card>
          <p className="text-ink-500 text-sm">Loading…</p>
        </Card>
      )}

      {user && tab === "profile" && <ProfileTab user={user} />}
      {user && tab === "projects" && <ProjectsTab userId={userId} />}
      {user && tab === "insurance" && (
        <InsuranceTab userId={userId} ownerships={user.ownerships} />
      )}
      {user && tab === "documents" && <DocumentsTab userId={userId} />}
      {user && tab === "invites" && <InvitesTab userId={userId} />}
      {user && tab === "correspondence" && <CorrespondenceTab userId={userId} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile tab — read-only summary. Edits stay on /admin/users (admin
// list page) to avoid duplicating the existing role / ownership /
// password tooling here.
// ---------------------------------------------------------------------------
function ProfileTab({ user }: { user: UserDetail }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Profile</h3>
        <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3 text-[13px]">
          <Label>Email</Label>
          <Value>{user.email}</Value>
          <Label>Phone</Label>
          <Value>{user.phone ?? "—"}</Value>
          <Label>Active</Label>
          <Value>
            {user.isActive ? (
              <span className="badge bg-status-approved-bg text-status-approved-fg">
                yes
              </span>
            ) : (
              <span className="badge bg-sand-100 text-ink-600">no</span>
            )}
          </Value>
          <Label>Email verified</Label>
          <Value>
            {user.emailVerifiedAt
              ? new Date(user.emailVerifiedAt).toLocaleDateString()
              : "—"}
          </Value>
          <Label>Last login</Label>
          <Value>
            {user.lastLoginAt
              ? new Date(user.lastLoginAt).toLocaleString()
              : "never"}
          </Value>
          <Label>Member since</Label>
          <Value>{new Date(user.createdAt).toLocaleDateString()}</Value>
        </dl>
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Roles</h3>
        {user.roles.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">No role grants.</p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {user.roles.map((r) => (
              <li key={r.id} className="py-2 flex items-center gap-3 flex-wrap">
                <span className="badge bg-brand-100 text-brand-700">
                  {r.roleName}
                </span>
                {r.implicit && (
                  <span
                    className="badge bg-sand-100 text-ink-500 text-[10.5px]"
                    title="Inferred from an active unit ownership; no explicit role grant on file"
                  >
                    via ownership
                  </span>
                )}
                <span className="text-[12.5px] text-ink-700 inline-flex items-center gap-1.5">
                  <Building2 size={13} className="text-ink-400" />
                  {r.buildingName}
                </span>
                <span className="ml-auto text-[11px] text-ink-400">
                  {r.implicit ? "since" : "granted"}{" "}
                  {new Date(r.grantedAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="lg:col-span-2">
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Units owned</h3>
        {user.ownerships.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">No units assigned.</p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {user.ownerships.map((o) => (
              <li key={o.id} className="py-2 flex items-center gap-3 flex-wrap">
                <span className="font-medium text-ink-800 text-[13px]">
                  Unit {o.unitNumber}
                </span>
                <span className="text-[11.5px] text-ink-500">
                  {o.buildingName}
                </span>
                <span className="badge bg-sand-100 text-ink-600">
                  {o.ownershipType}
                </span>
                <span className="ml-auto text-[11px] text-ink-400">
                  since {new Date(o.startedAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <dt className="text-[11.5px] uppercase tracking-wide text-ink-400">
      {children}
    </dt>
  );
}
function Value({ children }: { children: React.ReactNode }) {
  return <dd className="text-ink-700 m-0">{children}</dd>;
}

// ---------------------------------------------------------------------------
// Projects tab
// ---------------------------------------------------------------------------
type ProjectRow = {
  id: string;
  title: string;
  buildingId: string;
  buildingName: string;
  unitId: string;
  unitNumber: string;
  statusCode: string;
  projectTypeName: string;
  submittedAt: string | null;
  createdAt: string;
  relationship: ReadonlyArray<"owner" | "submitter" | "on_behalf_of">;
};

const RELATIONSHIP_LABEL: Record<ProjectRow["relationship"][number], string> = {
  owner: "Owner",
  submitter: "Submitter",
  on_behalf_of: "On behalf of",
};

function ProjectsTab({ userId }: { userId: string }) {
  const [rows, setRows] = useState<ProjectRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: ProjectRow[] }>(
        `/api/management/users/${userId}/projects`,
      )
      .then((r) => setRows(r.data))
      .catch((err) =>
        setError(err instanceof ApiError ? `Load failed (${err.status})` : String(err)),
      );
  }, [userId]);

  if (error) return <Card><p className="text-status-rejected-fg text-sm">{error}</p></Card>;
  if (rows === null) return <Card><p className="text-ink-500 text-sm">Loading…</p></Card>;
  if (rows.length === 0)
    return (
      <Card>
        <p className="text-ink-500 text-sm m-0">
          No projects linked to this user in your managed buildings.
        </p>
      </Card>
    );

  return (
    <Card className="!p-0 overflow-hidden">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Building / unit</th>
              <th>Status</th>
              <th>Relationship</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <td className="font-medium text-ink-800">{p.title}</td>
                <td className="text-ink-600">{p.projectTypeName}</td>
                <td className="text-ink-600 text-[12.5px]">
                  {p.buildingName}
                  <span className="text-ink-400"> · Unit {p.unitNumber}</span>
                </td>
                <td>
                  <span className="badge bg-sand-100 text-ink-700">
                    {p.statusCode.replace(/_/g, " ")}
                  </span>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {p.relationship.map((r) => (
                      <span
                        key={r}
                        className="badge bg-brand-100 text-brand-700"
                      >
                        {RELATIONSHIP_LABEL[r]}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="text-[12px] text-ink-500">
                  {new Date(p.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <Link
                    to={`/projects/${p.id}`}
                    className="text-brand-700 hover:text-brand-800 text-[12.5px] no-underline hover:no-underline"
                  >
                    Open
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Insurance tab — re-uses the existing AdminUserInsurance flow as a
// link rather than duplicating the multipart upload form here.
// ---------------------------------------------------------------------------
type Policy = {
  id: string;
  unitId: string;
  carrierName: string;
  policyNumber: string;
  perIncidentLimitCents: string;
  effectiveDate: string;
  expirationDate: string;
  validationStatus: string;
};

function InsuranceTab({
  userId,
  ownerships,
}: {
  userId: string;
  ownerships: Ownership[];
}) {
  const [policies, setPolicies] = useState<Policy[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: Policy[] }>(
        `/api/management/users/${userId}/liability-policies`,
      )
      .then((r) => setPolicies(r.data))
      .catch((err) =>
        setError(err instanceof ApiError ? `Load failed (${err.status})` : String(err)),
      );
  }, [userId]);

  if (error) return <Card><p className="text-status-rejected-fg text-sm">{error}</p></Card>;
  if (policies === null) return <Card><p className="text-ink-500 text-sm">Loading…</p></Card>;

  const unitNumberById = new Map(ownerships.map((o) => [o.unitId, o.unitNumber]));
  const now = new Date();
  const current = policies.filter((p) => new Date(p.expirationDate) >= now);
  const expired = policies.filter((p) => new Date(p.expirationDate) < now);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="text-ink-800 m-0 text-[15px]">Liability policies</h3>
          {ownerships.length > 0 && (
            <Link
              to={`/admin/users/${userId}/insurance`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] text-brand-700 hover:bg-brand-50 no-underline hover:no-underline"
            >
              <ShieldCheck size={13} /> Upload on this owner's behalf
            </Link>
          )}
        </div>
        <PolicyTable
          title="Current"
          policies={current}
          unitNumberById={unitNumberById}
          empty="No active policy on file."
        />
      </Card>
      {expired.length > 0 && (
        <Card>
          <h3 className="text-ink-800 m-0 mb-3 text-[15px]">
            Expired policies
          </h3>
          <PolicyTable
            title="Expired"
            policies={expired}
            unitNumberById={unitNumberById}
            empty="—"
          />
        </Card>
      )}
    </div>
  );
}

function PolicyTable({
  policies,
  unitNumberById,
  empty,
}: {
  title: string;
  policies: Policy[];
  unitNumberById: Map<string, string>;
  empty: string;
}) {
  if (policies.length === 0) {
    return <p className="text-ink-500 text-[13px] m-0">{empty}</p>;
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Unit</th>
            <th>Carrier</th>
            <th>Policy #</th>
            <th>Limit</th>
            <th>Effective</th>
            <th>Expiration</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {policies.map((p) => (
            <tr key={p.id}>
              <td className="font-medium text-ink-800">
                {unitNumberById.get(p.unitId) ?? "—"}
              </td>
              <td>{p.carrierName}</td>
              <td className="font-mono text-[12px]">{p.policyNumber}</td>
              <td>${(Number(p.perIncidentLimitCents) / 100).toLocaleString()}</td>
              <td className="text-[12px] text-ink-500">
                {new Date(p.effectiveDate).toLocaleDateString()}
              </td>
              <td className="text-[12px] text-ink-500">
                {new Date(p.expirationDate).toLocaleDateString()}
              </td>
              <td>
                <span className="badge bg-sand-100 text-ink-700">
                  {p.validationStatus}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invites tab
// ---------------------------------------------------------------------------
type IssuedInvite =
  | {
      id: string;
      kind: "building";
      buildingId: string;
      buildingName: string;
      roleCode: string;
      invitedEmail: string;
      issuedAt: string;
      expiresAt: string;
      redeemedAt: string | null;
      revokedAt: string | null;
    }
  | {
      id: string;
      kind: "contractor";
      projectId: string;
      projectTitle: string;
      buildingId: string;
      buildingName: string;
      contractorCompanyId: string;
      contractorCompanyName: string;
      invitedEmail: string;
      issuedAt: string;
      expiresAt: string;
      exchangedAt: string | null;
      revokedAt: string | null;
    };

type ReceivedInvite = Extract<IssuedInvite, { kind: "building" }>;

function InvitesTab({ userId }: { userId: string }) {
  const [data, setData] = useState<{
    issued: IssuedInvite[];
    received: ReceivedInvite[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{
        data: { issued: IssuedInvite[]; received: ReceivedInvite[] };
      }>(`/api/management/users/${userId}/invites`)
      .then((r) => setData(r.data))
      .catch((err) =>
        setError(err instanceof ApiError ? `Load failed (${err.status})` : String(err)),
      );
  }, [userId]);

  if (error) return <Card><p className="text-status-rejected-fg text-sm">{error}</p></Card>;
  if (!data) return <Card><p className="text-ink-500 text-sm">Loading…</p></Card>;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Issued by this user</h3>
        {data.issued.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">
            No invites issued in your managed buildings.
          </p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {data.issued.map((inv) => (
              <li
                key={`${inv.kind}-${inv.id}`}
                className="py-2.5 flex items-center gap-3 flex-wrap"
              >
                <span
                  className={cx(
                    "badge",
                    inv.kind === "building"
                      ? "bg-brand-100 text-brand-700"
                      : "bg-sand-100 text-ink-700",
                  )}
                >
                  {inv.kind === "building" ? "Building invite" : "Contractor"}
                </span>
                <span className="text-[12.5px] text-ink-700">
                  {inv.invitedEmail}
                </span>
                <span className="text-[11.5px] text-ink-500">
                  {inv.kind === "building"
                    ? `${inv.buildingName} · ${inv.roleCode.replace(/_/g, " ")}`
                    : `${inv.contractorCompanyName} · ${inv.projectTitle}`}
                </span>
                <span className="ml-auto text-[11px] text-ink-400">
                  {inviteStatus(inv)} ·{" "}
                  {new Date(inv.issuedAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Received by this user</h3>
        {data.received.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">
            No invites received at this user's email.
          </p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {data.received.map((inv) => (
              <li key={inv.id} className="py-2.5 flex items-center gap-3 flex-wrap">
                <span className="badge bg-brand-100 text-brand-700">
                  Building invite
                </span>
                <span className="text-[12.5px] text-ink-700">
                  {inv.buildingName} · {inv.roleCode.replace(/_/g, " ")}
                </span>
                <span className="ml-auto text-[11px] text-ink-400">
                  {inviteStatus(inv)} ·{" "}
                  {new Date(inv.issuedAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function inviteStatus(inv: IssuedInvite | ReceivedInvite): string {
  if (inv.revokedAt) return "revoked";
  if (inv.kind === "building" && inv.redeemedAt) return "redeemed";
  if (inv.kind === "contractor" && inv.exchangedAt) return "exchanged";
  if (new Date(inv.expiresAt) < new Date()) return "expired";
  return "active";
}

// ---------------------------------------------------------------------------
// Correspondence tab
// ---------------------------------------------------------------------------
type NotificationRow = {
  id: string;
  channel: string;
  templateCode: string;
  status: string;
  projectId: string | null;
  projectTitle: string | null;
  createdAt: string;
  sentAt: string | null;
  readAt: string | null;
  payload: Record<string, unknown> | null;
};

type CommentRow = {
  id: string;
  projectId: string;
  projectTitle: string;
  body: string;
  visibility: string;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
};

function CorrespondenceTab({ userId }: { userId: string }) {
  const [data, setData] = useState<{
    notifications: NotificationRow[];
    comments: CommentRow[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: { notifications: NotificationRow[]; comments: CommentRow[] } }>(
        `/api/management/users/${userId}/correspondence`,
      )
      .then((r) => setData(r.data))
      .catch((err) =>
        setError(err instanceof ApiError ? `Load failed (${err.status})` : String(err)),
      );
  }, [userId]);

  if (error) return <Card><p className="text-status-rejected-fg text-sm">{error}</p></Card>;
  if (!data) return <Card><p className="text-ink-500 text-sm">Loading…</p></Card>;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">
          Notifications & emails
        </h3>
        {data.notifications.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">
            No notifications recorded for this user in your managed buildings.
          </p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {data.notifications.map((n) => (
              <li key={n.id} className="py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={cx(
                      "badge",
                      n.channel === "email"
                        ? "bg-brand-100 text-brand-700"
                        : "bg-sand-100 text-ink-700",
                    )}
                  >
                    {n.channel}
                  </span>
                  <span className="font-mono text-[12px] text-ink-700">
                    {n.templateCode}
                  </span>
                  <span className="badge bg-sand-100 text-ink-600">
                    {n.status}
                  </span>
                  {n.projectTitle && (
                    <Link
                      to={`/projects/${n.projectId}`}
                      className="text-[12px] text-brand-700 hover:text-brand-800 no-underline hover:no-underline"
                    >
                      {n.projectTitle}
                    </Link>
                  )}
                  <span className="ml-auto text-[11px] text-ink-400">
                    {new Date(n.createdAt).toLocaleString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Comments authored</h3>
        {data.comments.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">
            No comments authored by this user in your managed buildings.
          </p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {data.comments.map((c) => (
              <li key={c.id} className="py-2.5">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span
                    className={cx(
                      "badge",
                      c.visibility === "internal"
                        ? "bg-status-review-bg text-status-review-fg"
                        : "bg-sand-100 text-ink-700",
                    )}
                  >
                    {c.visibility}
                  </span>
                  <Link
                    to={`/projects/${c.projectId}`}
                    className="text-[12.5px] text-brand-700 hover:text-brand-800 no-underline hover:no-underline"
                  >
                    {c.projectTitle}
                  </Link>
                  <span className="ml-auto text-[11px] text-ink-400">
                    {new Date(c.createdAt).toLocaleString()}
                    {c.editedAt && " · edited"}
                    {c.deletedAt && " · deleted"}
                  </span>
                </div>
                <p
                  className={cx(
                    "text-[13px] m-0 whitespace-pre-wrap",
                    c.deletedAt ? "text-ink-400 italic" : "text-ink-700",
                  )}
                >
                  {c.deletedAt ? "(deleted)" : c.body}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// Used implicitly via JSX.Element in TAB_ORDER
// ---------------------------------------------------------------------------
// Documents tab — permissions to enter, indemnity agreements, and
// dual-signature acknowledgements (Construction Rules, Procedures,
// License Agreement, Satellite Dish) for the user's units. Insurance
// has its own tab and is intentionally not duplicated here.
// ---------------------------------------------------------------------------
type PermissionToEnter = {
  id: string;
  projectId: string;
  projectTitle: string;
  unitNumber: string;
  permissionType: string;
  effectiveDate: string;
  recurringThrough: string | null;
  grantedToCompanyName: string | null;
  grantedToName: string | null;
  signedAt: string;
  revokedAt: string | null;
};
type IndemnityAgreement = {
  id: string;
  projectId: string;
  projectTitle: string;
  unitNumber: string;
  signerFullName: string;
  signedAt: string;
  bindingOnSuccessors: boolean;
};
type AckRow = {
  id: string;
  projectId: string;
  projectTitle: string;
  documentTypeCode: string;
  ownerSignedAt: string | null;
  contractorSignedAt: string | null;
  contractorCompanyName: string | null;
  createdAt: string;
};

const ACK_LABEL: Record<string, string> = {
  rules_acknowledgement: "Construction Rules",
  procedures_acknowledgement: "Procedures",
  license_agreement: "License Agreement",
  satellite_dish_agreement: "Satellite Dish",
};

function DocumentsTab({ userId }: { userId: string }) {
  const [data, setData] = useState<{
    permissionsToEnter: PermissionToEnter[];
    indemnityAgreements: IndemnityAgreement[];
    acknowledgements: AckRow[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{
        data: {
          permissionsToEnter: PermissionToEnter[];
          indemnityAgreements: IndemnityAgreement[];
          acknowledgements: AckRow[];
        };
      }>(`/api/management/users/${userId}/documents`)
      .then((r) => setData(r.data))
      .catch((err) =>
        setError(
          err instanceof ApiError ? `Load failed (${err.status})` : String(err),
        ),
      );
  }, [userId]);

  if (error)
    return (
      <Card>
        <p className="text-status-rejected-fg text-sm m-0">{error}</p>
      </Card>
    );
  if (!data)
    return (
      <Card>
        <p className="text-ink-500 text-sm m-0">Loading…</p>
      </Card>
    );

  const totalCount =
    data.permissionsToEnter.length +
    data.indemnityAgreements.length +
    data.acknowledgements.length;

  if (totalCount === 0) {
    return (
      <Card>
        <p className="text-ink-500 text-sm m-0">
          No permission, indemnity, or acknowledgement documents on file for
          this user's units in your managed buildings.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Permissions to enter</h3>
        {data.permissionsToEnter.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">None on file.</p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {data.permissionsToEnter.map((p) => {
              const grantee =
                p.grantedToCompanyName ?? p.grantedToName ?? "—";
              const status = p.revokedAt
                ? "revoked"
                : p.recurringThrough &&
                    new Date(p.recurringThrough) < new Date()
                  ? "expired"
                  : "active";
              return (
                <li
                  key={p.id}
                  className="py-2.5 flex items-center gap-3 flex-wrap"
                >
                  <span
                    className={cx(
                      "badge",
                      status === "active"
                        ? "bg-status-approved-bg text-status-approved-fg"
                        : status === "revoked"
                          ? "bg-status-rejected-bg text-status-rejected-fg"
                          : "bg-sand-100 text-ink-600",
                    )}
                  >
                    {status}
                  </span>
                  <span className="badge bg-sand-100 text-ink-700">
                    {p.permissionType.replace("_", " ")}
                  </span>
                  <span className="text-[12.5px] text-ink-700">{grantee}</span>
                  <Link
                    to={`/projects/${p.projectId}`}
                    className="text-[12px] text-brand-700 hover:text-brand-800 no-underline hover:no-underline"
                  >
                    {p.projectTitle}
                  </Link>
                  <span className="text-[11.5px] text-ink-500">
                    Unit {p.unitNumber}
                  </span>
                  <span className="ml-auto text-[11px] text-ink-400">
                    signed {new Date(p.signedAt).toLocaleDateString()}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Indemnity agreements</h3>
        {data.indemnityAgreements.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">None on file.</p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {data.indemnityAgreements.map((i) => (
              <li
                key={i.id}
                className="py-2.5 flex items-center gap-3 flex-wrap"
              >
                <span className="badge bg-status-approved-bg text-status-approved-fg">
                  signed
                </span>
                {i.bindingOnSuccessors && (
                  <span
                    className="badge bg-status-review-bg text-status-review-fg"
                    title="Binding on successors in interest and grantees of the unit"
                  >
                    binds successors
                  </span>
                )}
                <span className="text-[12.5px] text-ink-700">
                  {i.signerFullName}
                </span>
                <Link
                  to={`/projects/${i.projectId}`}
                  className="text-[12px] text-brand-700 hover:text-brand-800 no-underline hover:no-underline"
                >
                  {i.projectTitle}
                </Link>
                <span className="text-[11.5px] text-ink-500">
                  Unit {i.unitNumber}
                </span>
                <span className="ml-auto text-[11px] text-ink-400">
                  signed {new Date(i.signedAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-3 text-[15px]">Acknowledgements</h3>
        {data.acknowledgements.length === 0 ? (
          <p className="text-ink-500 text-[13px] m-0">None on file.</p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {data.acknowledgements.map((a) => {
              const ownerSigned = !!a.ownerSignedAt;
              const counterpartySigned = !!a.contractorSignedAt;
              const fullySigned =
                ownerSigned &&
                (a.contractorCompanyName === null || counterpartySigned);
              return (
                <li
                  key={a.id}
                  className="py-2.5 flex items-center gap-3 flex-wrap"
                >
                  <span
                    className={cx(
                      "badge",
                      fullySigned
                        ? "bg-status-approved-bg text-status-approved-fg"
                        : ownerSigned
                          ? "bg-status-review-bg text-status-review-fg"
                          : "bg-sand-100 text-ink-600",
                    )}
                  >
                    {fullySigned
                      ? "complete"
                      : ownerSigned
                        ? "awaiting counterparty"
                        : "pending"}
                  </span>
                  <span className="text-[12.5px] text-ink-700">
                    {ACK_LABEL[a.documentTypeCode] ?? a.documentTypeCode}
                  </span>
                  {a.contractorCompanyName && (
                    <span className="text-[11.5px] text-ink-500">
                      with {a.contractorCompanyName}
                    </span>
                  )}
                  <Link
                    to={`/projects/${a.projectId}`}
                    className="ml-auto text-[12px] text-brand-700 hover:text-brand-800 no-underline hover:no-underline"
                  >
                    {a.projectTitle}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

void Button;
